// store/printerSlice.ts
import {createSlice, createAsyncThunk, PayloadAction} from "@reduxjs/toolkit";
import {addBleDevice, getBleDevice, removeBleDevice} from "./referencias/bleRegistry";

// interface PrinterState {
//     device: BluetoothDevice | null;
//     characteristic: BluetoothRemoteGATTCharacteristic | null;
//     isLoading: boolean;
//     error: string | null;
// }

interface PrinterState {
    openedConfigPrinter: boolean;
    deviceId: string | null;
    deviceName: string | null;
    model: string | null;
    papelWidth: string | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: PrinterState = {
    openedConfigPrinter: false,
    deviceId: null,
    deviceName: null,
    model: "mpt-ii",
    papelWidth: null,
    isLoading: false,
    error: null,
};
const serviceUuid = ["000018f0-0000-1000-8000-00805f9b34fb"]
// 🔹 Conectar impresora
export const connectPrinter = createAsyncThunk<
    { deviceId: string; deviceName: string },
    void,
    { rejectValue: string }
>("printer/connect", async (_, { rejectWithValue, dispatch }) => {
    try {
        const device = await navigator.bluetooth.requestDevice({
            filters: [{ services: serviceUuid }],
        });

        const server = await device.gatt?.connect();
        if (!server) throw new Error("No se pudo conectar al servidor GATT");

        const service = await server.getPrimaryService(
            serviceUuid[0]
        );

        const characteristics = await service.getCharacteristics();
        const writable = characteristics.find(
            (c) => c.properties.write || c.properties.writeWithoutResponse
        );
        if (!writable) throw new Error("No se encontró característica writable");

        // Guardamos referencia en bleRegistry
        addBleDevice(device.id, device, writable);

        device.addEventListener("gattserverdisconnected", () => {
            removeBleDevice(device.id);
            dispatch(resetPrinter()); // 👈 aquí resetea Redux
        });

        return { deviceId: device.id, deviceName: device.name ?? "Desconocido" };
    } catch (err) {
        return rejectWithValue((err as Error).message);
    }
});
// 🔹 Desconectar impresora
export const disconnectPrinter = createAsyncThunk<
    void,
    void,
    { state: any; rejectValue: string }
>("printer/disconnect", async (_, { getState, rejectWithValue }) => {
    const { deviceId } = getState().printer;
    if (!deviceId) return rejectWithValue("No hay dispositivo conectado");

    const entry = removeBleDevice(deviceId);
    try {
        entry?.device.gatt?.disconnect();
    } catch (err) {
        return rejectWithValue((err as Error).message);
    }
});

// 🔹 Imprimir
export const print = createAsyncThunk<
    void,
    Uint8Array,
    { state: any; rejectValue: string }
>("printer/print", async (data, { getState, rejectWithValue }) => {
    const { deviceId } = getState().printer;
    if (!deviceId) return rejectWithValue("No hay dispositivo conectado");

    const entry = getBleDevice(deviceId);
    if (!entry) return rejectWithValue("Referencia BLE no encontrada");

    try {
        const { characteristic } = entry;
        const chunkSize = 20;
        for (let i = 0; i < data.length; i += chunkSize) {

            const chunk = data.slice(i, i + chunkSize);
            if (characteristic.properties.writeWithoutResponse) {
                await characteristic.writeValueWithoutResponse(chunk);
            } else {
                await characteristic.writeValue(chunk);
            }

        }
    } catch (err) {
        console.log(err)
        return rejectWithValue((err as Error).message);
    }
});

const printerSlice = createSlice({
    name: "printer",
    initialState,
    reducers: {
        setPrinterModel(state, action: PayloadAction<string>) {
            state.model = action.payload;
        },
        setPrinterPaperWidth(state, action: PayloadAction<string>) {
            state.papelWidth = action.payload;
        },
        toggleOpenedConfig(state) {
            state.openedConfigPrinter = !state.openedConfigPrinter;
        },
        resetPrinter: (state) => {
            state.deviceId = null;
            state.deviceName = null;
            state.model = null;
            state.papelWidth = null;
            state.isLoading = false;
            state.error = null;
            state.openedConfigPrinter = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(connectPrinter.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(connectPrinter.fulfilled, (state, action) => {
                state.deviceId = action.payload.deviceId;
                state.deviceName = action.payload.deviceName;
                state.isLoading = false;
            })
            .addCase(connectPrinter.rejected, (state, action) => {
                state.error =
                    (action.payload as string) || action.error.message || "Error desconocido";
                state.isLoading = false;
            })
            .addCase(disconnectPrinter.fulfilled, (state) => {
                state.deviceId = null;
                state.deviceName = null;
            })
            .addCase(disconnectPrinter.rejected, (state, action) => {
                state.error =
                    (action.payload as string) || action.error.message || "Error desconocido";
            })
            .addCase(print.rejected, (state, action) => {
                state.error =
                    (action.payload as string) || action.error.message || "Error al imprimir";
            });
    },
});

export const { setPrinterModel ,setPrinterPaperWidth, toggleOpenedConfig, resetPrinter} = printerSlice.actions;
export default printerSlice.reducer;
